import { useId } from "react";
import { Link, useLocation } from "react-router-dom";
import useAuthStore from "../store/authStore";
import useThemeStore from "../store/themeStore";

function Mark({ color, maskId }) {
  return (
    <>
      {/* Counter is masked out rather than filled, so any background shows through */}
      <mask id={maskId}>
        <rect x="0" y="0" width="120" height="120" fill="white" />
        <path d="M 51 69 Q 51 76 61 76 Q 68 76 68 69 C 68 62 51 62 51 69 Z" fill="black" />
      </mask>
      <g mask={`url(#${maskId})`}>
        <path
          d="M 68 44 L 32 44 L 32 84 L 84 84 L 84 28 L 20 28 L 20 96 L 96 96 L 96 16 L 8 16 L 8 108 L 108 108"
          stroke={color} strokeWidth="5.5" strokeLinecap="square" strokeLinejoin="miter" fill="none"
        />
        <line x1="60" y1="44" x2="74" y2="44" stroke={color} strokeWidth="6" strokeLinecap="square" />
        <line x1="74" y1="44" x2="74" y2="70" stroke={color} strokeWidth="6" strokeLinecap="square" />
        <path d="M 74 70 Q 74 83 61 83 Q 44 83 44 72" stroke={color} strokeWidth="6" strokeLinecap="square" fill="none" />
        <path d="M 44 72 C 44 56 74 56 74 70" stroke={color} strokeWidth="6" strokeLinecap="square" fill="none" />
      </g>
    </>
  );
}

export default function Logo({ className = "h-12" }) {
  const isDark = useThemeStore((s) => s.isDark);
  const location = useLocation();
  const seekerToken = useAuthStore((s) => s.seekerToken);
  const employerToken = useAuthStore((s) => s.employerToken);

  // Unique per instance, so multiple logos on one page do not share a mask id.
  const maskId = useId();

  // Surface decides intent: which "home" does the user expect from here?
  // Auth store decides allowance: fall back to public if missing the relevant token.
  const path = location.pathname;
  let to = "/";
  if (path.startsWith("/employers") || path.startsWith("/employer")) {
    to = employerToken ? "/employers/dashboard" : "/";
  } else if (path.startsWith("/app")) {
    to = seekerToken ? "/app/home" : "/";
  } else {
    // Public path: fall back to authenticated home if there is one.
    // If both tokens exist, prefer seeker home as the more common "personal" landing.
    if (seekerToken) {
      to = "/app/home";
    } else if (employerToken) {
      to = "/employers/dashboard";
    }
  }

  // Single ink color for the whole lockup. The mark is monochrome by design,
  // the violet accent is spent on actions rather than on the logo.
  const color = isDark ? "#FFFFFF" : "#111318";
  const muted = isDark ? "#9CA3AF" : "#6B7280";

  return (
      <Link to={to} aria-label="Joborigo home">
        {/* Icon only below sm. Square viewBox, so it takes the width it needs. */}
        <svg
          viewBox="0 0 120 120"
          xmlns="http://www.w3.org/2000/svg"
          className={`${className} w-auto sm:hidden`}
          role="img"
          aria-label="Joborigo"
        >
          <Mark color={color} maskId={`${maskId}-icon`} />
        </svg>

        {/* Full lockup from sm up */}
        <svg
          viewBox="0 0 460 120"
          xmlns="http://www.w3.org/2000/svg"
          className={`${className} w-auto hidden sm:block`}
          role="img"
          aria-label="Joborigo"
        >
          <Mark color={color} maskId={`${maskId}-lockup`} />
          <text
            x="148"
            y="82"
            fill={color}
            fontFamily="Oxanium, Trebuchet MS, sans-serif"
            fontWeight="800"
            fontSize="60"
            letterSpacing="-0.5"
          >
            Job<tspan fill={muted}>origo</tspan>
          </text>
        </svg>
      </Link>
    );
}
